import { Card, Accordion, Text, Link, Icon, Separator, CardColors } from '@/ui'
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { useReselector } from '@/utils/sharedHooks'
import { selectUserHasPermission } from '@/reducers/auth/permission.selectors'
import {
  ProductFeatureId,
  startUserPermissionsRefresh,
} from '@/actions/permissionActions'
import { useAppDispatch } from '@/utils/typeHelpers'
import { Attributes, useAnalyticsView } from '@/features/amplitude'
import { useEffect } from 'react'
import { TypographyStyles } from '@/styles'

const HelpOption = ({
  title,
  description,
  linkText = 'Go',
  href,
  newPage,
  trackMessage,
  trackAttributes,
}: {
  title: string
  description: string
  linkText?: string
  href?: string
  newPage?: boolean
  trackMessage?: string
  trackAttributes?: Attributes
}) => {
  return (
    <div className="mb-4 mt-4">
      <Text variant={TypographyStyles.H3} className="text-neutral-700 mb-1">
        {title}
      </Text>
      <Text
        variant={TypographyStyles.BODY_SM}
        className="text-neutral-600 mb-1"
      >
        {description}{' '}
        {href && (
          <Link
            variant="noUnderline"
            href={href}
            newPage={newPage}
            trackMessage={trackMessage}
            trackAttributes={trackAttributes}
          >
            <b>{linkText}</b>
            <Icon icon={faArrowRight} className="ml-1.5" size="sm" />
          </Link>
        )}
      </Text>
    </div>
  )
}

export const GetHelpCard = () => {
  const hasPhoneSupport = useReselector(
    selectUserHasPermission,
    ProductFeatureId.phoneSupport
  )
  const dispatch = useAppDispatch()
  const pageView = useAnalyticsView()

  useEffect(() => startUserPermissionsRefresh(dispatch), [dispatch])

  useEffect(() => {
    pageView('viewed get help card')
  }, [pageView])

  return (
    <Card color={CardColors.LEVEL_0}>
      <Accordion variant="header" type="single" collapsible>
        <Accordion.Item value="help">
          <Accordion.Trigger>
            <div className="flex flex-col w-full">
              <Text variant={TypographyStyles.H2}>Get Help</Text>
              <Text
                variant={TypographyStyles.BODY_MD}
                className="text-neutral-500"
              >
                How to get help at Heard
              </Text>
            </div>
          </Accordion.Trigger>
          <Accordion.Content>
            <div className="space-y-4">
              <HelpOption
                title="Help Center"
                description="Search articles to address common and frequently asked questions."
                href="https://support.joinheard.com/hc/en-us"
                newPage
                trackMessage="clicked help center on get help card"
                trackAttributes={{ button: 'help center' }}
              />
              <Separator />
              <HelpOption
                title="Messages"
                description="Send a message to a member of our Support team to get personalized help."
                href="/conversations"
                trackMessage="clicked messages on get help card"
                trackAttributes={{ button: 'messages' }}
              />
              {hasPhoneSupport && (
                <>
                  <Separator />
                  <HelpOption
                    title="Schedule a Call"
                    description="Need to talk with our support team? Pick an available time and we’ll call you then. We’ve moved from a live phone line to scheduled calls, so there’s no waiting on hold."
                    linkText="Schedule here"
                    href="https://calendly.com/heard-phone-support/heard-support-callback"
                    newPage
                    trackMessage="clicked schedule a call on get help card"
                    trackAttributes={{ button: 'schedule a call' }}
                  />
                </>
              )}
            </div>
          </Accordion.Content>
        </Accordion.Item>
      </Accordion>
    </Card>
  )
}
